import { useMemo } from "react";
import { formatCustomerDisplayName } from "@/domain/customers/formatCustomer";
import {
  periodShortcutRange,
  type PeriodShortcut,
} from "@/domain/invoices/datePeriodShortcuts";
import type { InvoiceFilterValues } from "@/domain/invoices/invoiceFilterState";
import {
  resolveBulkExportCustomersHint,
  validateInvoiceFilterValues,
} from "@/domain/invoices/invoiceFilterState";
import { FilterValidationAlert } from "@/features/invoices/components/FilterValidationAlert";
import { INVOICE_STATUS_MULTI_OPTIONS } from "@/domain/invoices/invoiceStatus";
import { CREDIT_NOTE_STATUS_OPTIONS } from "@/domain/creditNotes/creditNoteStatus";
import { AmountRangeFilter } from "@/features/invoices/components/AmountRangeFilter";
import { CustomerMultiSelect } from "@/features/invoices/components/CustomerMultiSelect";
import { DateRangeFilter } from "@/features/invoices/components/DateRangeFilter";
import { MultiSelect } from "@/features/invoices/components/MultiSelect";
import type { CustomerRow } from "@/types/api";
import { cn } from "@/utils/cn";

const PERIOD_OPTIONS: { key: PeriodShortcut; label: string }[] = [
  { key: "last-month", label: "Last month" },
  { key: "this-month", label: "This month" },
  { key: "last-30-days", label: "Last 30 days" },
];

interface BulkExportFiltersPanelProps {
  filters: InvoiceFilterValues;
  activePeriod: PeriodShortcut | null;
  customers: CustomerRow[];
  customersLoading?: boolean;
  customersError?: string | null;
  onChange: (next: InvoiceFilterValues) => void;
  onPeriodSelect: (period: PeriodShortcut) => void;
  onReloadCustomers?: () => void;
  variant?: "invoice" | "credit_note";
}

export function BulkExportFiltersPanel({
  filters,
  activePeriod,
  customers,
  customersLoading,
  customersError,
  onChange,
  onPeriodSelect,
  onReloadCustomers,
  variant = "invoice",
}: BulkExportFiltersPanelProps) {
  const customerOptions = useMemo(
    () =>
      customers.map((c) => ({
        value: c.id,
        title: formatCustomerDisplayName(c),
        subtitle: c.email ? `${c.email} · ${c.id}` : c.id,
      })),
    [customers],
  );

  const statusOptions = useMemo(
    () =>
      (variant === "credit_note"
        ? CREDIT_NOTE_STATUS_OPTIONS
        : INVOICE_STATUS_MULTI_OPTIONS
      ).map((o) => ({
        value: o.value,
        label: o.label,
      })),
    [variant],
  );

  const patch = (partial: Partial<InvoiceFilterValues>) =>
    onChange({ ...filters, ...partial });

  const filterIssues = validateInvoiceFilterValues(filters);
  const customersHint = resolveBulkExportCustomersHint(filters.customerIds);

  return (
    <div className="filters-panel">
      <div className="mb-3">
        <h3 className="text-[0.95rem] font-semibold">Export filters</h3>
      </div>

      <div className="filter-period-shortcuts">
        <span className="period-label">Quick period</span>
        {PERIOD_OPTIONS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={cn("period-chip", activePeriod === key && "is-active")}
            onClick={() => {
              onPeriodSelect(key);
              const range = periodShortcutRange(key);
              patch({ dateFrom: range.dateFrom, dateTo: range.dateTo });
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {customersError ? (
        <p className="mb-2 text-sm text-[var(--muted)]">
          {customersError}
          {onReloadCustomers ? (
            <>
              {" "}
              <button
                type="button"
                className="font-semibold underline"
                onClick={() => onReloadCustomers()}
              >
                Retry
              </button>
            </>
          ) : null}
        </p>
      ) : null}

      <div
        className="filter-row-download-filters filter-row-invoices filter-row-invoices--bulk-export overflow-visible"
        lang="en"
      >
        <div className="filter-slot-customers">
          <CustomerMultiSelect
            label="Customers"
            options={customerOptions}
            selected={filters.customerIds}
            onChange={(customerIds) => patch({ customerIds })}
            allLabel="All customers"
            searchPlaceholder="Search by email or name…"
            disabled={customersLoading}
            loading={customersLoading}
          />
        </div>

        <div className="filter-slot-status">
          <MultiSelect
            label="Status"
            options={statusOptions}
            selected={filters.statuses}
            onChange={(statuses) => patch({ statuses })}
            allLabel="All statuses"
          />
        </div>

        <AmountRangeFilter
          amountMin={filters.amountMin}
          amountMax={filters.amountMax}
          onChange={(partial) => patch(partial)}
        />

        <div className="filter-slot-created">
          <DateRangeFilter
            label={variant === "credit_note" ? "CN created" : "Invoice created"}
            dateFrom={filters.dateFrom}
            dateTo={filters.dateTo}
            disableFuture
            onChange={(partial) =>
              patch({
                dateFrom: partial.dateFrom ?? filters.dateFrom,
                dateTo: partial.dateTo ?? filters.dateTo,
              })
            }
          />
        </div>

        {variant === "invoice" ? (
          <div className="filter-slot-due">
            <DateRangeFilter
              label="Due date"
              dateFrom={filters.dueDateFrom}
              dateTo={filters.dueDateTo}
              onChange={(partial) =>
                patch({
                  dueDateFrom: partial.dateFrom ?? filters.dueDateFrom,
                  dueDateTo: partial.dateTo ?? filters.dueDateTo,
                })
              }
            />
          </div>
        ) : null}
      </div>

      {customersHint ? (
        <p className="filter-customers-hint" role="status">
          {customersHint}
        </p>
      ) : null}

      {filterIssues.length > 0 ? (
        <div className="mt-3">
          <FilterValidationAlert messages={filterIssues} />
        </div>
      ) : null}
    </div>
  );
}
